import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Space, Spin, Table, Tag, Typography, message } from "antd";
import dayjs from "dayjs";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import { getErrorText } from "../../utils/errorText";
import { confirmDanger } from "../../componets/common/confirmDanger";

const { Text, Paragraph } = Typography;

interface UnifiedConversation {
  id: number;
  scope: string;
  title: string;
  projectId?: number;
  createdAt: string;
  updatedAt?: string;
}

interface ChatMessage {
  id: number;
  role: string;
  content: string;
  created_at?: string;
  createdAt?: string;
}

/**
 * WidgetSessionsTab 对话/挂件会话 Tab(PRD v4 03§6 聊天记录双 Tab 之二):
 * 消费统一会话 API(/api/v3/conversations?scope=chat),详情抽屉 + 删除。
 */
const WidgetSessionsTab: React.FC = () => {
  const { API_BASE_URL, projects } = useGlobal();
  const [items, setItems] = useState<UnifiedConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [activeTitle, setActiveTitle] = useState("");

  const projectName = (id?: number) => projects.find((p) => p.id === id)?.name || (id ? `项目 ${id}` : "-");

  const fetchSessions = useCallback(
    async (page = 1, pageSize = 20) => {
      setLoading(true);
      try {
        const res = await request.get(`${API_BASE_URL}/api/v3/conversations`, {
          params: { scope: "chat", page, pageSize },
        });
        const data = res.data?.data;
        setItems(data?.items || []);
        setPagination({ current: page, pageSize, total: data?.total || 0 });
      } catch (e) {
        message.error(getErrorText(e, "加载会话列表失败"));
      } finally {
        setLoading(false);
      }
    },
    [API_BASE_URL]
  );

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const openMessages = async (record: UnifiedConversation) => {
    setActiveTitle(record.title || `会话 ${record.id}`);
    setDrawerOpen(true);
    setMessagesLoading(true);
    setMessages([]);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/conversations/chat/${record.id}/messages`);
      setMessages(res.data?.data || []);
    } catch (e) {
      message.error(getErrorText(e, "加载消息失败"));
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleDelete = (record: UnifiedConversation) => {
    confirmDanger({
      title: "删除会话?",
      consequence: `将永久删除会话「${record.title || record.id}」及其全部消息,不可恢复。`,
      okText: "确认删除",
      onOk: async () => {
        try {
          const res = await request.delete(`${API_BASE_URL}/api/v3/conversations/chat/${record.id}`);
          if (res.data.code === 200) {
            message.success("已删除");
            fetchSessions(pagination.current, pagination.pageSize);
          } else {
            message.error(res.data.msg || "删除失败");
          }
        } catch (e) {
          message.error(getErrorText(e, "删除失败"));
        }
      },
    });
  };

  return (
    <>
      <Table<UnifiedConversation>
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={items}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条会话`,
          onChange: (page, pageSize) => fetchSessions(page, pageSize),
        }}
        columns={[
          {
            title: "会话",
            dataIndex: "title",
            ellipsis: true,
            render: (v, r) => v || `会话 ${r.id}`,
          },
          {
            title: "项目",
            dataIndex: "projectId",
            width: 180,
            render: (v) => <Tag color="blue">{projectName(v)}</Tag>,
          },
          {
            title: "创建时间",
            dataIndex: "createdAt",
            width: 170,
            render: (v) => dayjs(v).format("YYYY-MM-DD HH:mm:ss"),
          },
          {
            title: "操作",
            key: "actions",
            width: 160,
            render: (_, record) => (
              <Space>
                <Button size="small" onClick={() => openMessages(record)}>
                  查看
                </Button>
                <Button size="small" danger onClick={() => handleDelete(record)}>
                  删除
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Drawer title={activeTitle} width={560} open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Spin spinning={messagesLoading}>
          {messages.length === 0 && !messagesLoading ? (
            <Text type="secondary">此会话暂无消息</Text>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                style={{
                  marginBottom: 12,
                  display: "flex",
                  flexDirection: m.role === "user" ? "row-reverse" : "row",
                }}
              >
                <div
                  style={{
                    maxWidth: "85%",
                    background: m.role === "user" ? "#e6f4ff" : "#f6f6f6",
                    borderRadius: 8,
                    padding: "8px 12px",
                  }}
                >
                  <Paragraph style={{ marginBottom: 4, whiteSpace: "pre-wrap" }}>{m.content}</Paragraph>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {m.role} · {dayjs(m.created_at || m.createdAt).format("MM-DD HH:mm:ss")}
                  </Text>
                </div>
              </div>
            ))
          )}
        </Spin>
      </Drawer>
    </>
  );
};

export default WidgetSessionsTab;
